import { useEffect, useId, useRef, useState } from "react";
import { useField } from "formik";
import style from "./FormUpload.module.css";
import { FolderArrowUp } from "../UI/Icons";
import useAuthenticatedAxios from "../../hooks/use-authenticated-axios";
import Callout from "../UI/Callout";
import { classnames, handleAPIError } from "../../utils/index.jsx";
import ListFiles from "../UI/ListFiles";
import {
  canPreprocessImage,
  preprocessImage,
  validateUploadedFile,
  watermarkPdf,
} from "../../utils/documents";
import { ACCEPTED_FILE_EXTENSIONS } from "../../utils/constants/documents";
import { formatFileSize } from "../../api/Functions";
import { useUploadLock } from "../UI/useUploadLock";
import UploadProgress from "../UI/UploadProgress";

/** @deprecated Use FileInput */
const FormUpload = ({
  tag,
  status,
  valid_until,
  label,
  path,
  edit,
  text = "Déposez vos fichiers",
  fileType = "file",
  shouldWatermark = false,
  warning,
  noTagFilter,
  hideList = false,
}) => {
  const axios = useAuthenticatedAxios();
  const [{ value }, , { setValue }] = useField(path || "files");
  const valueRef = useRef(value);
  const id = useId();
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [highlight, setHighlight] = useState(false);
  const { acquire } = useUploadLock();

  useEffect(() => {
    if (!valueRef.current) {
      setValue([]);
      valueRef.current = [];
    }
  }, [setValue]);

  async function handleSubmit(tag, event, valid_until, status, isDrop) {
    event.preventDefault();
    setHighlight(false);
    const formData = new FormData();
    const selectedFiles = isDrop
      ? event.dataTransfer.files
      : event.target.files;
    // Sélection annulée ou dépôt sans fichier : rien à envoyer
    if (selectedFiles.length === 0) {
      return;
    }

    // Le formulaire reste verrouillé jusqu'à la réponse du serveur : c'est elle
    // qui rattache les fichiers au formulaire.
    const release = acquire();
    setUploading(true);
    setLoading(0);
    try {
      for (const file of selectedFiles) {
        const validationError = validateUploadedFile(file);
        if (validationError) {
          setError(validationError);
          return;
        }

        if (canPreprocessImage(file)) {
          let preprocessedImage = await preprocessImage(file, shouldWatermark);
          formData.append("selectedFiles", preprocessedImage);
        } else if (file.type === "application/pdf" && shouldWatermark) {
          const watermarkedPDF = await watermarkPdf(file);
          formData.append("selectedFiles", watermarkedPDF);
        } else {
          formData.append("selectedFiles", file);
        }
      }

      formData.append("tag", tag);
      if (valid_until) {
        formData.append("valid_until", valid_until);
      }
      if (status) {
        formData.append("status", status);
      }

      const res = await axios.post("/files/upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: function (progressEvent) {
          let percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total,
          );
          setLoading(percentCompleted);
        },
      });
      const newFiles = res.data.map((file, i) => {
        return {
          ...file,
          localFile: selectedFiles[i],
        };
      });
      setValue([...(value || []), ...(newFiles || [])]);
      event.target.value = null;
      setError("");
    } catch (error) {
      setError(handleAPIError(error));
    } finally {
      setUploading(false);
      release();
    }
  }

  async function deleteFileHandler(fileToDelete) {
    setValue((value || []).filter((file) => file.src !== fileToDelete.src));
  }
  const totalFileSize = value?.reduce((total, file) => total + file.size, 0);

  return (
    <div className={"marginT10"}>
      {label && <p>{label}</p>}
      {!edit && (
        <>
          <input
            type="file"
            onChange={(evt) =>
              handleSubmit(tag, evt, valid_until, status, false)
            }
            id={id}
            className={style.inputFile}
            accept={ACCEPTED_FILE_EXTENSIONS}
            multiple
          />

          <label
            htmlFor={id}
            className="flex align-items-center justify-content-flex-start"
          >
            <div
              className={classnames(
                style.wrapper,
                highlight && style.highlight,
              )}
              onDragLeave={() => setHighlight(false)}
              onDrop={(e) => handleSubmit(tag, e, valid_until, status, true)}
              onDragOver={(e) => {
                e.preventDefault();
                setHighlight(true);
              }}
            >
              <div className={style.icon}>
                <FolderArrowUp stroke="#0C69F5" />
              </div>
              <div>
                <p className={style.primary}>{text}</p>
                <p className={`${style.secondary} marginR10`}>
                  Format PNG, JPG, JPEG, PDF
                </p>
              </div>
            </div>
          </label>
          <UploadProgress
            percent={loading}
            uploading={uploading}
            processingText={
              fileType === "edl"
                ? "Traitement des photos en cours, patientez avant d'enregistrer…"
                : undefined
            }
          />
          <Callout variant="danger">{error}</Callout>
        </>
      )}

      <br />

      {value && !hideList && (
        <ListFiles
          files={noTagFilter ? value : value.filter((file) => file.tag === tag)}
          fileType={fileType}
          onDelete={edit ? null : deleteFileHandler}
        />
      )}
      {totalFileSize > 9900000 && warning && (
        <Callout variant="danger">
          Attention, la taille totale des documents fait{" "}
          {formatFileSize(totalFileSize)}. Au dela de 10 Mo, il est probable que
          votre mail ne soit pas reçu par le destinataire.
        </Callout>
      )}
    </div>
  );
};

export default FormUpload;
